import type { AddressUsage } from '~/api/DeliveryAddress';
import type { PickupPoint } from '~/components/ModalDeliveryAddress/GMap/interfaces';

/**
 * Pickup point behind a record that carries a resolved fulfilment type and a
 * warehouse code, or undefined when nothing identifies one (zones are also
 * unavailable during SSR, before the query resolves).
 *
 * Deliberately stricter than `findPickupPointForAddress`
 * (hooks/usePickupZones): that one falls back to the nearest point at any
 * distance, which is right when the customer
 * is picking a point on a map and wrong when we are labelling a record they
 * already saved — an address whose warehouse code was never stored would
 * silently take the name and assortment of whichever point happens to be
 * closest. Here an exact warehouse-code match is the only match.
 *
 * The usage must already be resolved through `resolveAddressUsage` (kept out
 * of this module so it stays free of the company singleton). The usage check
 * guards the other direction: a courier address carries the code of the
 * warehouse serving it, and that warehouse may also run a pickup zone, which
 * would otherwise label a home address as a pickup point.
 */
export function matchPickupPoint(
  usage: AddressUsage,
  warehouseCode: string | null | undefined,
  points: PickupPoint[],
): PickupPoint | undefined {
  if (!warehouseCode || usage !== 'pickup') {
    return undefined;
  }

  return points.find((point) => point.warehouseCode === warehouseCode);
}
